import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  SortDirection,
  UserPermission,
  useNotify
} from '@globaleris/ui.library'
import { Delete, Edit, History, ListAlt, PlayArrow, Receipt, Timelapse } from '@material-ui/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumTipoCicloFacturacion, EnumTipoFacturacion } from '../../../../api'
import { InvoiceCycleWithLastExecutionModel } from '../../../../api/Argentia/endpoints/invoiceCyclesExecutions/model/cfExecutionModel'
import { EnumEstadoCiclo } from '../../../../api/oData/enums/enumEstadoCiclo'
import { EnumSubtipoCiclo } from '../../../../api/oData/enums/enumSubtipoCiclo'
import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import { enumToSelectOptionArray } from '../../../../helpers'
import { getUserPermissions } from '../../../../helpers/permissionsHelper'
import { useConfirmationDialog, useHttpError, usePageState } from '../../../../hooks'
import { NonAuthRoutes } from '../../../../model'
import { InformationDialogs } from '../../../../model/enums/informationDialogs'
import { Pages } from '../../../../model/RBAC/constants'
import { getCustomerDomain } from '../../../../redux'
import { CycleExecutionModel } from '../../invoiceCycles.model'
import {
  deleteInvoiceCycle,
  fetchFinishedExecutionsByInvoiceCycleId,
  fetchInvoiceCyclesExecutions
} from '../../invoiceCycles.service'
import { ExecutionHistoryDialog } from './executionHistoryDialog'

export interface AllCyclesTableProps {
  refreshTrigger?: number
  subjectType?: number
  activesOnly?: boolean
  commodity?: number
  onCreateCycle?: () => void
  onEditCycle?: (cycle: InvoiceCycleWithLastExecutionModel) => void
  onStartExecution?: (cycle: InvoiceCycleWithLastExecutionModel) => void
  onRefreshData?: () => void
  onDeleteSuccess?: () => void
}

export const AllCyclesTable: React.FC<AllCyclesTableProps> = ({
  refreshTrigger,
  subjectType,
  activesOnly,
  commodity,
  onCreateCycle,
  onEditCycle,
  onStartExecution,
  onRefreshData,
  onDeleteSuccess
}) => {
  const [t] = useTranslation([
    'globalerisLibrary',
    'informationDialogs',
    'enums',
    'invoiceCyclesPage'
  ])
  const history = useHistory()
  const { showError, clearError, errorProps } = useHttpError()
  const { dataTableFilter, dataTableSearch, dateFilter, customFilter, activeTab } = usePageState()
  const { getInformation, closeInformation } = useConfirmationDialog()
  const { error: notifyError } = useNotify()

  const [cycles, setCycles] = useState<InvoiceCycleWithLastExecutionModel[] | null>(null)
  const [cyclesWithActiveExecutions, setCyclesWithActiveExecutions] = useState<Set<number>>(
    new Set()
  )
  const [historyData, setHistoryData] = useState<CycleExecutionModel[]>([])
  const [showHistoryDialog, setShowHistoryDialog] = useState<boolean>(false)
  const [selectedCycleName, setSelectedCycleName] = useState<string>('')
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false)

  const handleOnCreateCycle = async () => {
    if (onCreateCycle) {
      onCreateCycle()
    }
    return true
  }

  const onViewSettlementsActionHandler = (row: InvoiceCycleWithLastExecutionModel) => {
    if (!row || !row.lastExecutionId) return
    const isAnnulationOrRefacturation =
      row.invoiceType === EnumTipoFacturacion.Anuladora ||
      row.invoiceType === EnumTipoFacturacion.Refacturacion
    if (isAnnulationOrRefacturation) {
      history.push(NonAuthRoutes.SettlementsAnnulation, {
        executionCycleId: row.lastExecutionId,
        invoiceCycleId: row.invoiceCycleId,
        billingType: row.invoiceType,
        startDate: row.lastExecutionStartDate,
        endDate: row.lastExecutionEndDate,
        reInvoiceType: row.lastExecutionReInvoiceType,
        groupingType: row.lastExecutionGroupingType,
        isSettlements: true,
        isSettlementsInvoice: false
      })
    } else {
      history.push(NonAuthRoutes.Settlements, {
        executionCycleId: row.lastExecutionId,
        invoiceCycleId: row.invoiceCycleId
      })
    }
  }

  const onReviewInvoicesActionHandler = (row: InvoiceCycleWithLastExecutionModel) => {
    if (!row || !row.lastExecutionId) return
    history.push(NonAuthRoutes.SettlementsInvoices, {
      executionCycleId: row.lastExecutionId,
      invoiceCycleId: row.invoiceCycleId,
      reInvoiceType: row.lastExecutionReInvoiceType,
      groupingType: row.lastExecutionGroupingType,
      isSettlements: false,
      isSettlementsInvoice: true
    })
  }

  const onViewProgressActionHandler = (row: InvoiceCycleWithLastExecutionModel) => {
    if (!row || !row.lastExecutionId) return
    history.push(NonAuthRoutes.invoiceCycleProcessing, {
      executionCycleId: row.lastExecutionId,
      invoiceCycleId: row.invoiceCycleId,
      cycleName: row.description,
      billingType: row.invoiceType,
      originState: row.lastExecutionStatus
    })
  }

  const onStartExecutionClickHandler = (row: InvoiceCycleWithLastExecutionModel) => {
    if (onStartExecution) {
      onStartExecution(row)
    }
  }

  const onEditCycleClickHandler = async (row: InvoiceCycleWithLastExecutionModel) => {
    if (onEditCycle) {
      onEditCycle(row)
    }
  }

  const onDeleteCycleClickHandler = async (row: InvoiceCycleWithLastExecutionModel) => {
    if (cyclesWithActiveExecutions.has(row.invoiceCycleId)) {
      notifyError(
        t(
          'invoiceCyclesPage:delete.errorActiveExecution',
          'No se puede eliminar el ciclo porque tiene ejecuciones activas'
        )
      )
      return
    }

    await getInformation(
      InformationDialogs.INVOICE_CYCLES_CONFIRM_DELETE,
      t('informationDialogs:INVOICE_CYCLES_CONFIRM_DELETE.message', {
        cycleName: row.description
      })
    ).then(async () => {
      closeInformation()

      const response = await deleteInvoiceCycle(row.invoiceCycleId)

      if (response.success) {
        // Call the success callback first (parent will show toast)
        if (onDeleteSuccess) {
          onDeleteSuccess()
        }
        // Then refresh data (which will trigger child components to re-mount)
        if (onRefreshData) {
          onRefreshData()
        }
      } else {
        console.error('Error deleting invoice cycle:', response.error)
        notifyError(
          t('invoiceCyclesPage:delete.error', 'Error al eliminar el ciclo de facturación')
        )
      }
    })
  }

  const onViewHistoryClickHandler = async (row: InvoiceCycleWithLastExecutionModel) => {
    setSelectedCycleName(row.description || '')
    setShowHistoryDialog(true)
    setIsLoadingHistory(true)

    const response = await fetchFinishedExecutionsByInvoiceCycleId(row.invoiceCycleId)

    if (response.success && response.data) {
      setHistoryData(response.data)
    } else {
      setHistoryData([])
    }

    setIsLoadingHistory(false)
  }

  const handleCloseHistoryDialog = () => {
    setShowHistoryDialog(false)
    setHistoryData([])
    setSelectedCycleName('')
    setIsLoadingHistory(false)
  }

  const columns = React.useMemo((): DataTableColumn[] => {
    return [
      { name: 'invoiceCycleId', display: false },
      {
        name: 'description',
        label: t('invoiceCyclesPage:allCyclesTable.columns.cycleName'),
        type: DataType.TEXT
      },
      {
        name: 'invoiceCycleType',
        label: t('invoiceCyclesPage:allCyclesTable.columns.type'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(
          EnumTipoCicloFacturacion,
          'enums:EnumTipoCicloFacturacion'
        )
      },
      {
        name: 'invoiceCycleSubType',
        label: t('invoiceCyclesPage:allCyclesTable.columns.subtype'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumSubtipoCiclo, 'enums:EnumSubtipoCiclo')
      },
      {
        name: 'invoiceType',
        label: t('invoiceCyclesPage:allCyclesTable.columns.invoiceType'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoFacturacion, 'enums:EnumTipoFacturacion')
      },
      {
        name: 'lastExecutionSubType',
        label: t('invoiceCyclesPage:activeCyclesTable.columns.lastExecutionSubtype'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumSubtipoCiclo, 'enums:EnumSubtipoCiclo')
      },
      {
        name: 'lastExecutionStartDate',
        label: t('invoiceCyclesPage:activeCyclesTable.columns.fromDate'),
        type: DataType.DATE
      },
      {
        name: 'lastExecutionEndDate',
        label: t('invoiceCyclesPage:activeCyclesTable.columns.toDate'),
        type: DataType.DATE
      },
      {
        name: 'lastExecutionDate',
        label: t('invoiceCyclesPage:activeCyclesTable.columns.executionDate'),
        type: DataType.DATE
      },
      {
        name: 'lastExecutionStatus',
        label: t('invoiceCyclesPage:activeCyclesTable.columns.lastExecutionStatus'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumEstadoCiclo, 'enums:EnumEstadoCiclo')
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const options = React.useMemo((): DataTableOptions => {
    return {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      search: true,
      elevation: 1,
      filterType: FilterType.ADVANCED,
      download: true,
      canEdit: false,
      canDelete: false,
      pagination: true,
      rowsPerPage: 7,
      rowsPerPageOptions: [7, 10, 20, 50, 100],
      customInsertEditModal: true,
      sortOrder: {
        name: 'lastExecutionDate',
        direction: SortDirection.DESC
      },
      analytics: {
        customer: getCustomerDomain(),
        view: t('invoicesPage:activeCyclesTable.analytics')
      },
      onRowInsert: handleOnCreateCycle,
      rowCustomActions: [
        {
          icon: <PlayArrow fontSize="small" />,
          tooltip: t('invoiceCyclesPage:mainActions.triggerCycle'),
          onClick: onStartExecutionClickHandler,
          userPermission: UserPermission.EDIT,
          visibleIf: 'canLaunch'
        },
        {
          icon: <ListAlt fontSize="small" />,
          tooltip: t('invoiceCyclesPage:allCyclesTable.tooltips.viewSettlements', 'Ver listado'),
          onClick: onViewSettlementsActionHandler,
          userPermission: UserPermission.EDIT,
          visibleIf: 'canViewDetails'
        },
        {
          icon: <Receipt fontSize="small" />,
          tooltip: t('invoiceCyclesPage:allCyclesTable.tooltips.reviewInvoices', 'Revisar facturas'),
          onClick: onReviewInvoicesActionHandler,
          userPermission: UserPermission.EDIT,
          visibleIf: 'canViewDetails'
        },
        {
          icon: <Timelapse fontSize="small" />,
          tooltip: t('invoiceCyclesPage:allCyclesTable.tooltips.viewProgress', 'Ver progreso'),
          onClick: onViewProgressActionHandler,
          userPermission: UserPermission.EDIT,
          visibleIf: 'canViewProgress'
        },
        {
          icon: <History fontSize="small" />,
          tooltip: t('invoiceCyclesPage:allCyclesTable.tooltips.viewHistory', 'Ver historial'),
          onClick: onViewHistoryClickHandler,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('invoiceCyclesPage:allCyclesTable.tooltips.edit', 'Editar ciclo'),
          onClick: onEditCycleClickHandler,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('invoiceCyclesPage:allCyclesTable.tooltips.delete', 'Eliminar ciclo'),
          onClick: onDeleteCycleClickHandler,
          userPermission: UserPermission.EDIT,
          visibleIf: 'canDelete'
        }
      ]
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
    // }, [tableConfiguration, props.commodity, activeBalanceZone, activeCompany])
  }, [])

  const fetchInvoiceCycles = useCallback(async () => {
    setCycles(null)
    const response = await fetchInvoiceCyclesExecutions(subjectType, activesOnly, commodity)
    if (response?.data) {
      setCycles(response.data)
      // Mark cycles that have active (non-finalized, non-annulled) executions
      setCyclesWithActiveExecutions(
        new Set(
          response.data
            .filter(
              cycle =>
                cycle.lastExecutionStatus !== null &&
                cycle.lastExecutionStatus !== EnumEstadoCiclo.Finalizado &&
                cycle.lastExecutionStatus !== EnumEstadoCiclo.Anulado
            )
            .map(cycle => cycle.invoiceCycleId)
        )
      )
    } else {
      setCycles([])
      setCyclesWithActiveExecutions(new Set())
    }
  }, [subjectType, activesOnly, commodity])

  useEffect(() => {
    fetchInvoiceCycles()
  }, [fetchInvoiceCycles, refreshTrigger])

  const enhancedCycles = React.useMemo(() => {
    if (!cycles) return null
    const showDetailsStatuses = [
      EnumEstadoCiclo.PdteValidarListado,
      EnumEstadoCiclo.PdteValidarFacturas,
      EnumEstadoCiclo.Procesando
    ]
    return cycles.map(cycle => ({
      ...cycle,
      canDelete: !cyclesWithActiveExecutions.has(cycle.invoiceCycleId),
      canLaunch: !cyclesWithActiveExecutions.has(cycle.invoiceCycleId),
      canViewDetails:
        cycle.lastExecutionStatus !== null &&
        showDetailsStatuses.includes(cycle.lastExecutionStatus),
      canViewProgress: cycle.lastExecutionStatus === EnumEstadoCiclo.Procesando
    }))
  }, [cycles, cyclesWithActiveExecutions])

  return (
    <>
      <DataTable
        title={''}
        data={enhancedCycles}
        columns={columns}
        options={options}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.InvoicesPage)}
        appliedFilter={dataTableFilter}
        appliedSearch={dataTableSearch}
      />
      <ExecutionHistoryDialog
        open={showHistoryDialog}
        cycleName={selectedCycleName}
        historyData={historyData}
        isLoading={isLoadingHistory}
        onClose={handleCloseHistoryDialog}
      />
    </>
  )
}
